import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function MenopauseFHPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-pink-50 rounded-full mb-6">
              <span className="text-pink-600 font-semibold text-sm">Women's Health</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Menopause and Familial Hypercholesterolemia (FH):<br />
              <span className="text-pink-600">A Critical Turning Point for Women's Heart Health</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
              For many women, menopause marks a major life transition — hormonally, physically, and metabolically.
            </p>
          </div>

          {/* Introduction */}
          <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              For women living with <strong>Familial Hypercholesterolemia (FH)</strong>, menopause is more than a hormonal milestone. It can represent a <strong>pivotal shift in cardiovascular risk</strong>.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Because FH already causes lifelong elevation of LDL ("bad") cholesterol, the protective effects of estrogen before menopause play an important role. When estrogen levels decline, cardiovascular risk often accelerates — and in women with FH, that acceleration can be significant.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              <strong>Understanding this relationship is key to prevention and awareness.</strong>
            </p>
          </div>

          {/* Understanding Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl p-8 border-2 border-pink-200">
              <div className="w-14 h-14 bg-pink-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">How Menopause Affects Cholesterol</h3>
              
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Before menopause, estrogen helps:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-pink-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">Increase HDL ("good") cholesterol</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-pink-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">Support healthy endothelial function</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-pink-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">Improve arterial flexibility</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-pink-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">Reduce LDL oxidation</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">After menopause:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-rose-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">LDL cholesterol often rises</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-rose-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">HDL may decline</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-rose-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">Triglycerides may increase</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-rose-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">Arterial stiffness progresses</span>
                  </li>
                </ul>
              </div>

              <p className="text-gray-700 mt-6 font-medium">
                Even in women without FH, cardiovascular risk increases after menopause. In women with FH, this shift can be more pronounced.
              </p>
            </div>

            <div className="bg-gradient-to-br from-rose-50 to-red-100 rounded-2xl p-8 border-2 border-rose-200">
              <div className="w-14 h-14 bg-rose-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-alert-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Menopause Matters More in FH</h3>
              
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">In Heterozygous Familial Hypercholesterolemia (HeFH):</h4>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-rose-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">LDL has been elevated since birth</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-rose-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">Arteries may already contain plaque</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-rose-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">Subclinical atherosclerosis may be present</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white/60 rounded-xl p-6 mb-6">
                <p className="text-gray-700 mb-3">
                  <strong>Before menopause:</strong> Estrogen may partially offset some vascular damage.
                </p>
                <p className="text-gray-700">
                  <strong>After menopause:</strong>
                </p>
                <ul className="space-y-2 mt-2">
                  <li className="flex items-start">
                    <i className="ri-close-circle-fill text-red-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">The loss of estrogen removes that protective buffer</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-circle-fill text-red-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">Plaque progression may accelerate</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-circle-fill text-red-600 mt-1 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">Risk of coronary events rises</span>
                  </li>
                </ul>
              </div>

              <p className="text-gray-900 font-bold">
                This makes the peri-menopausal and post-menopausal years a critical period for cardiovascular reassessment.
              </p>
            </div>
          </div>

          {/* Hero Image */}
          <div className="rounded-2xl overflow-hidden mb-16 shadow-xl">
            <img 
              src="https://public.readdy.ai/ai/img_res/1814c2f1aae6e5e0925a8ea53eb67f8c.jpg"
              alt="Menopause and Heart Health in FH"
              className="w-full h-[500px] object-cover object-top"
            />
          </div>

          {/* Key Interactions */}
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Key Cardiovascular Changes During Menopause in FH</h2>

          {/* Section 1 */}
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8 mb-8 border-2 border-red-200">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white font-bold text-lg">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">Rising LDL After Menopause</h3>
            </div>
            
            <p className="text-gray-700 mb-4 leading-relaxed">
              Many women notice their cholesterol increases during menopause.
            </p>

            <div className="bg-white/60 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-3">For women with FH:</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-arrow-up-circle-fill text-red-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">LDL may rise further from already elevated levels</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-up-circle-fill text-red-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Non-HDL cholesterol increases</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-up-circle-fill text-red-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Lipoprotein(a) may remain elevated</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4 font-medium">
                This can push some women into higher-risk categories if lipid management is not optimized.
              </p>
            </div>
          </div>

          {/* Section 2 */}
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 mb-8 border-2 border-orange-200">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-orange-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white font-bold text-lg">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">Increased Atherosclerosis Progression</h3>
            </div>
            
            <p className="text-gray-700 mb-4 leading-relaxed">
              Estrogen has anti-inflammatory and endothelial-protective properties.
            </p>

            <div className="bg-white/60 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-3">After menopause:</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-orange-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Endothelial dysfunction increases</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-orange-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Oxidative stress rises</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-orange-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Vascular inflammation intensifies</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4 font-medium">
                In FH, where LDL-driven plaque is already present, this hormonal change may accelerate plaque growth and instability.
              </p>
            </div>
          </div>

          {/* Section 3 */}
          <div className="bg-gradient-to-br from-rose-50 to-pink-100 rounded-2xl p-8 mb-8 border-2 border-rose-200">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-rose-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white font-bold text-lg">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">Coronary Artery Disease Risk After Menopause</h3>
            </div>
            
            <p className="text-gray-700 mb-4 leading-relaxed">
              Historically, women with FH experience coronary events later than men — often in their 50s rather than 30s or 40s.
            </p>

            <div className="bg-white/60 rounded-xl p-6 mb-4">
              <h4 className="font-semibold text-gray-900 mb-3">However, after menopause:</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-alert-fill text-rose-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Risk of myocardial infarction rises sharply</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-alert-fill text-rose-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Multi-vessel disease becomes more common</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-alert-fill text-rose-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Symptoms may be atypical</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-900 font-bold">
              Because women are often underdiagnosed or undertreated, awareness is essential.
            </p>
          </div>

          {/* Section 4 */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 mb-8 border-2 border-purple-200">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white font-bold text-lg">4</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">Hypertension and Metabolic Changes</h3>
            </div>
            
            <div className="bg-white/60 rounded-xl p-6 mb-4">
              <h4 className="font-semibold text-gray-900 mb-3">Menopause is associated with:</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-purple-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Increased blood pressure</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-purple-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Central weight gain</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-purple-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Insulin resistance</span>
                </li>
              </ul>
            </div>

            <div className="bg-white/60 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-3">When combined with FH:</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-error-warning-fill text-purple-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Vascular risk multiplies</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-error-warning-fill text-purple-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Plaque instability increases</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-error-warning-fill text-purple-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Long-term cardiovascular burden grows</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4 font-medium">
                This makes comprehensive risk management even more important.
              </p>
            </div>
          </div>

          {/* Section 5 */}
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 mb-8 border-2 border-teal-200">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white font-bold text-lg">5</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">Hormone Replacement Therapy (HRT) and FH</h3>
            </div>
            
            <div className="bg-white/60 rounded-xl p-6 mb-4">
              <h4 className="font-semibold text-gray-900 mb-3">Hormone replacement therapy may:</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Improve lipid profile modestly</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Raise HDL</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Lower LDL slightly</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6 mb-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-information-fill text-amber-600 mr-2"></i>
                However:
              </h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-amber-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">HRT is not a substitute for lipid-lowering therapy</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-amber-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">It is not recommended solely for cardiovascular prevention</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-amber-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Risks and benefits must be individualized</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-900 font-bold">
              Women with established coronary artery disease require careful evaluation before considering HRT.
            </p>
          </div>

          {/* Section 6 */}
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-8 mb-8 border-2 border-emerald-200">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white font-bold text-lg">6</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">Medication Optimization During Menopause</h3>
            </div>
            
            <p className="text-gray-700 mb-4 leading-relaxed">
              This life stage is an ideal time to:
            </p>

            <div className="bg-white/60 rounded-xl p-6 mb-4">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Reassess LDL targets</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Intensify statin therapy if needed</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Add ezetimibe or PCSK9 inhibitors</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Evaluate Lipoprotein(a) levels</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Screen for coronary calcium</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-900 font-bold">
              For very high-risk patients, LDL targets below 55 mg/dL may be appropriate.
            </p>
          </div>

          {/* Section 7 */}
          <div className="bg-gradient-to-br from-red-50 to-rose-100 rounded-2xl p-8 mb-8 border-2 border-red-200">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white font-bold text-lg">7</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">HoFH and Menopause</h3>
            </div>
            
            <div className="bg-white/60 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-3">In women with Homozygous Familial Hypercholesterolemia (HoFH):</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-alert-fill text-red-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Cardiovascular disease may develop much earlier</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-alert-fill text-red-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Menopause may further exacerbate vascular stiffness</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-alert-fill text-red-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Structural heart disease may already be present</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4 font-medium">
                These women require specialized cardiovascular care throughout life.
              </p>
            </div>
          </div>

          {/* Section 8 */}
          <div className="bg-gradient-to-br from-fuchsia-50 to-fuchsia-100 rounded-2xl p-8 mb-8 border-2 border-fuchsia-200">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-fuchsia-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white font-bold text-lg">8</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">Silent Risk in Women</h3>
            </div>
            
            <p className="text-gray-700 mb-4 leading-relaxed">
              Women often present with:
            </p>

            <div className="bg-white/60 rounded-xl p-6 mb-4">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-fuchsia-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Atypical chest pain</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-fuchsia-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Fatigue</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-fuchsia-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Shortness of breath</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-fuchsia-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Indigestion-like symptoms</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-900 font-bold">
              After menopause, these symptoms should not be dismissed — especially in women with elevated LDL or family history of early heart disease.
            </p>
          </div>

          {/* Section 9 */}
          <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl p-8 mb-8 border-2 border-cyan-200">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-cyan-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white font-bold text-lg">9</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">The Importance of Screening During Perimenopause</h3>
            </div>
            
            <p className="text-gray-700 mb-4 leading-relaxed">
              Perimenopause provides a critical opportunity for:
            </p>

            <div className="bg-white/60 rounded-xl p-6 mb-4">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-cyan-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Lipid testing</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-cyan-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Cardiovascular imaging (if indicated)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-cyan-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Blood pressure evaluation</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-cyan-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Glucose screening</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-900 font-bold">
              If LDL exceeds 190 mg/dL, or if there is a strong family history of early heart disease, evaluation for FH is warranted.
            </p>
          </div>

          {/* Section 10 */}
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-8 mb-8 border-2 border-indigo-200">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <span className="text-white font-bold text-lg">10</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">Long-Term Prevention</h3>
            </div>
            
            <p className="text-gray-700 mb-4 leading-relaxed">
              The key concept in FH is <strong>lifetime LDL exposure</strong>.
            </p>

            <p className="text-gray-700 mb-4 leading-relaxed">
              While menopause increases cardiovascular risk, the foundation for protection is built earlier in life.
            </p>

            <div className="bg-white/60 rounded-xl p-6 mb-4">
              <h4 className="font-semibold text-gray-900 mb-3">Early diagnosis of FH allows:</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-indigo-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Decades of LDL control before menopause</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-indigo-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Reduced plaque burden</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-indigo-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Lower event risk in post-menopausal years</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-900 font-bold">
              The earlier LDL is reduced, the better the long-term outcomes.
            </p>
          </div>

          {/* Raising Awareness */}
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-8 mb-12 border-2 border-amber-200">
            <div className="flex items-start mb-6">
              <div className="w-14 h-14 bg-amber-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-megaphone-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">Raising Awareness</h3>
            </div>
            
            <p className="text-gray-700 mb-4 leading-relaxed">
              Many women discover FH only after:
            </p>

            <div className="bg-white/60 rounded-xl p-6 mb-4">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-amber-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">A heart attack</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-amber-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">A sibling's early cardiac event</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-amber-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Elevated cholesterol detected during routine screening</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-900 font-bold mb-6">
              Menopause should be a trigger for renewed cardiovascular attention — not a time to overlook risk.
            </p>

            <div className="bg-white/60 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-3">If you have:</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">LDL above 190 mg/dL</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Family history of early heart disease</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Tendon xanthomas</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1 mr-2 flex-shrink-0"></i>
                  <span className="text-gray-700">Rising cholesterol after menopause</span>
                </li>
              </ul>
              <p className="text-gray-900 font-bold mt-4">
                Ask about Familial Hypercholesterolemia screening.
              </p>
            </div>
          </div>

          {/* Final Takeaway */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-10 text-white mb-12">
            <div className="text-center mb-8">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white/10 mx-auto mb-6">
                <i className="ri-lightbulb-line text-4xl text-white"></i>
              </div>
              <h2 className="text-3xl font-semibold mb-4">Final Takeaway</h2>
            </div>

            <div className="space-y-4 mb-8">
              <p className="text-lg text-white/90 leading-relaxed">
                Menopause naturally increases cardiovascular risk.
              </p>
              
              <p className="text-lg text-white/90 leading-relaxed">
                In women with Familial Hypercholesterolemia, that risk may accelerate more dramatically.
              </p>

              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm my-6">
                <p className="text-xl font-bold text-white mb-3">The good news?</p>
                <p className="text-base text-white/90 leading-relaxed">
                  With early diagnosis and aggressive LDL management, heart disease is largely preventable.
                </p>
              </div>

              <p className="text-lg text-white/90 leading-relaxed">
                Awareness during the menopausal transition can protect not just the next decade — but the rest of a woman's life.
              </p>

              <p className="text-xl font-bold text-white text-center mt-8">
                Because when it comes to FH, timing truly matters.
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-pink-600 to-rose-600 rounded-2xl p-12 text-center text-white mb-16">
            <h2 className="text-3xl font-bold mb-4">Ready to Optimize Your Heart Health?</h2>
            <p className="text-xl mb-8 text-pink-50">
              Schedule a consultation to discuss menopause management and FH treatment strategies
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/consultation" 
                className="inline-block px-8 py-4 bg-white text-pink-600 rounded-xl font-semibold hover:bg-pink-50 transition-colors whitespace-nowrap"
              >
                Schedule Consultation
              </Link>
              <Link 
                to="/topics" 
                className="inline-block px-8 py-4 bg-pink-700 text-white rounded-xl font-semibold hover:bg-pink-800 transition-colors whitespace-nowrap"
              >
                Explore More Topics
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
